import React, { useEffect, useState, useCallback } from "react";

// ── Set your event details here ──────────────────────────────────────────────

const EVENT_DATE = new Date("2026-12-19T16:00:00"); // ← updated: rescheduled from Aug 24
// ─────────────────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");

const formatEventDate = (date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const useCountdown = (target) => {
  const calc = useCallback(() => {
    const diff = target - new Date();
    if (diff <= 0) return { days: 0, hrs: 0, mins: 0, secs: 0, ended: true };
    return {
      days: Math.floor(diff / 86400000),
      hrs: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
      ended: false,
    };
  }, [target]);

  // Start with a fixed placeholder (matches what react-snap pre-renders),
  // then swap to the real live value after mount to avoid a hydration mismatch.
  const [time, setTime] = useState({ days: 0, hrs: 0, mins: 0, secs: 0, ended: false });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setTime(calc());
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);

  return mounted ? time : { days: 0, hrs: 0, mins: 0, secs: 0, ended: false };
};

// ── Numeric, colon-separated countdown (matches reference: big orange digits,
//    small grey uppercase labels underneath, colons vertically centered on the
//    digits, small dot marker at the end) ────────────────────────────────────
const CountdownUnit = ({ value, label }) => (
  <div className="ebc-cd-unit">
    <span className="ebc-cd-num">{pad(value)}</span>
    <span className="ebc-cd-lbl">{label}</span>
  </div>
);

const CountdownStrip = () => {
  const { days, hrs, mins, secs, ended } = useCountdown(EVENT_DATE);

  if (ended) {
    return (
      <div className="ebc-cd">
        <div className="ebc-cd-card">
          <span className="ebc-cd-ended">Event has started!</span>
        </div>
      </div>
    );
  }

  const units = [
    { value: days, label: "Days" },
    { value: hrs, label: "Hrs" },
    { value: mins, label: "Mins" },
    { value: secs, label: "Secs" },
  ];

  return (
    <div className="ebc-cd" role="timer" aria-label="Event countdown">
      {units.map((u) => (
        <div className="ebc-cd-card" key={u.label}>
          <CountdownUnit value={u.value} label={u.label} />
        </div>
      ))}
    </div>
  );
};

const EventBanner = () => {
  return (
    <>
      <style>{`

        #ebc-section {
          padding: 0 1rem 3rem;
          max-width: 1237px;
          margin: 0 auto;
        }

        .ebc-card {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          background: #ffffff;
          box-shadow: 0 4px 32px rgba(0,0,0,0.08);
          border: 2px solid rgba(255,255,255,0.98);
          outline: 1px solid rgba(0,0,0,0.06);
        }

        .ebc-gradient {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 70% 100% at -5% -5%, rgba(255,140,30,0.60) 0%, rgba(255,160,60,0.25) 35%, transparent 60%),
            radial-gradient(ellipse 60% 80% at 105% 110%, rgba(10,120,10,0.55) 0%, rgba(19,136,8,0.20) 35%, transparent 60%);
          pointer-events: none;
          z-index: 0;
        }

        .ebc-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,87,0,0.10);
          border: 1px solid rgba(255,87,0,0.28);
          border-radius: 30px;
          padding: 4px 13px;
          white-space: nowrap;
        }
        .ebc-badge-dot {
          width: 7px; height: 7px;
          background: #002a9b;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .ebc-badge-text {
          font-size: 10.5px;
          color: #002a9b;
          font-weight: 700;
          letter-spacing: 0.10em;
          text-transform: uppercase;
          font-family: 'Inter', sans-serif;
          white-space: nowrap;
        }
          .ebc-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0.9rem 0 0.2rem;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(243,124,35,0.08), rgba(0,42,155,0.05));
  border: 1px solid rgba(243,124,35,0.30);
  
}
.ebc-notice-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.ebc-notice-text {
  font-size: 12.5px;
  line-height: 1.5;
  color: #5a3d1a;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
}
.ebc-notice-text strong {
  color: #002a9b;
  font-weight: 700;
}
@media (max-width: 680px) {
  .ebc-notice { padding: 10px 12px; margin: 0.7rem 0 0.1rem; }
  .ebc-notice-text { font-size: 11.5px; }
}

        /* ══════════════════════════════
           COUNTDOWN — DIGITS + COLONS
        ══════════════════════════════ */
        .ebc-cd-card {
          display: inline-flex;
          background: #ffffff;
          border-radius: 14px;
          border: 1.5px solid rgba(243,124,35,0.35);
          box-shadow: 0 0 0 1px rgba(243,124,35,0.10), 0 6px 20px rgba(243,124,35,0.16), 0 2px 8px rgba(0,0,0,0.06);
          padding: 10px 4px;
          box-sizing: border-box;
        }
        .ebc-cd {
          display: flex;
          align-items: stretch;
          gap: 8px;
          width: fit-content;
          max-width: 100%;
          box-sizing: border-box;
        }
        .ebc-cd-unit {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
          padding: 0 12px;
          min-width: 46px;
        }
        .ebc-cd-num {
          font-family: 'Poppins', sans-serif;
          font-weight: 800;
          color: #f37c23;
          font-size: 34px;
          line-height: 1.05;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .ebc-cd-lbl {
          margin-top: 3px;
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9a9a9a;
          white-space: nowrap;
        }
        .ebc-btn-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #f37c23;
          flex-shrink: 0;
        }
        .ebc-cd-ended {
          font-size: 13px;
          font-weight: 700;
          color: #e8640a;
          font-family: 'Inter', sans-serif;
        }
        @media (max-width: 680px) {
          .ebc-cd { gap: 6px; }
          .ebc-cd-card { padding: 7px 3px; border-radius: 11px; }
          .ebc-cd-unit { padding: 0 8px; min-width: 34px; }
          .ebc-cd-num { font-size: 22px; }
          .ebc-cd-lbl { font-size: 8px; margin-top: 2px; }
        }
        @media (max-width: 420px) {
          .ebc-cd-unit { padding: 0 5px; min-width: 28px; }
          .ebc-cd-num { font-size: 18px; }
          .ebc-cd-lbl { font-size: 7px; }
        }

        /* hide everything by default */
        .ebc-desk-img,
        .ebc-desk-badge-wrap,
        .ebc-desk-btn,
        .ebc-desk-cd,
        .ebc-mob-toprow,
        .ebc-mob-2months,
        .ebc-mob-location-row,
        .ebc-mob-img-wrap,
        .ebc-mob-btn { display: none; }

        /* ══════════════════════════════
           DESKTOP  ≥ 681px
        ══════════════════════════════ */
        @media (min-width: 681px) {
          .ebc-card {
            display: flex;
            align-items: center;
            min-height: 300px;
          }
          .ebc-desk-img {
            display: block;
            position: absolute;
            top: 0; right: 0; bottom: 0; left: 42%;
            z-index: 1;
            pointer-events: none;
          }
          .ebc-desk-img img {
            width: 100%; height: 100%;
            object-fit: contain;
            object-position: center;
            opacity: 0.70;
            mix-blend-mode: multiply;
          }
          
          .ebc-desk-badge-wrap {
            display: block;
            position: absolute;
            top: 1.4rem; left: 2rem;
            z-index: 3;
          }
          .ebc-text {
            position: relative;
            z-index: 3;
            flex: 0 0 55%;
            padding: 4.5rem 2.8rem 2.8rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .ebc-location { display: flex; }
          .ebc-desk-btn {
            display: inline-flex;
            position: absolute;
            bottom: 1.6rem; right: 2rem;
            z-index: 4;
            align-items: center;
            gap: 10px;
            background: #fff;
            color: #111;
            font-size: 14px;
            font-weight: 700;
            padding: 8px 8px 8px 26px;
            border-radius: 30px;
            border: 1.5px solid rgba(0,0,0,0.10);
            text-decoration: none;
            box-shadow: 0 2px 16px rgba(0,0,0,0.12);
            white-space: nowrap;
            font-family: 'Inter', sans-serif;
          }

          /* countdown moves out of the text column and sits at the top-right corner of the card */
          .ebc-mob-cd { display: none; }
          .ebc-desk-cd {
            display: flex;
            justify-content: flex-end;
            position: absolute;
            top: 1.6rem;
            right: 2rem;
            z-index: 3;
            max-width: calc(100% - 4rem);
          }
        }

        /* ══════════════════════════════
           MOBILE  ≤ 680px
        ══════════════════════════════ */
        @media (max-width: 680px) {
          #ebc-section { padding: 0.5rem 1rem 1.5rem; }

          .ebc-card {
            display: flex;
            flex-direction: column;
          }

          .ebc-mob-toprow {
            display: flex;
            align-items: center;
            padding: 1.2rem 1rem 0;
            position: relative;
            z-index: 5;
          }

          .ebc-text {
            position: relative;
            z-index: 3;
            padding: 0.8rem 1.4rem 0.4rem;
            display: flex;
            flex-direction: column;
          }

          .ebc-location { display: none !important; }

          .ebc-mob-location-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 7px;
            padding: 0 1.4rem 0.5rem;
            font-size: 13px;
            font-weight: 600;
            color: #1a1a1a;
            font-family: 'Inter', sans-serif;
            position: relative;
            z-index: 4;
          }

          .ebc-mob-img-wrap {
            display: block;
            position: relative;
            z-index: 2;
            width: 100%;
            line-height: 0;
          }
          .ebc-mob-img-wrap img {
            width: 100%;
            height: auto;
            object-fit: contain;
            opacity: 0.80;
            mix-blend-mode: multiply;
            display: block;
          }

          .ebc-mob-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin: 0.8rem 1.4rem 1.2rem;
            padding: 8px 8px 8px 30px;
            border-radius: 30px;
            border: 1.5px solid rgba(0,0,0,0.15);
            background: #fff;
            color: #111;
            font-size: 14px;
            font-weight: 700;
            font-family: 'Inter', sans-serif;
            text-decoration: none;
            box-shadow: 0 2px 12px rgba(0,0,0,0.08);
            position: relative;
            z-index: 4;
          }

          .ebc-title { font-size: 26px !important; }
          .ebc-badge { padding: 4px 8px !important; gap: 4px !important; }
          .ebc-badge img { width: 11px !important; height: 11px !important; }
          .ebc-badge-text { font-size: 8.5px !important; letter-spacing: 0.06em !important; }

          .ebc-mob-cd {
            display: block;
            margin-top: 0.7rem;
          }
        }

        /* ══════════════════════════════
           DATE / LOCATION SPACING
        ══════════════════════════════ */
        .ebc-date-group,
        .ebc-place-group {
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .ebc-place-group {
          margin-left: 22px;
        }
        @media (max-width: 680px) {
          .ebc-mob-location-row { row-gap: 6px; }
          .ebc-place-group { margin-left: 0; flex-basis: 100%; }
        }
      `}</style>

      <section id="ebc-section">
        <div className="ebc-card">

          <div className="ebc-gradient" />

          {/* ── DESKTOP ONLY ── */}
          <div className="ebc-desk-img">
            <img src="/Event/Untitled design-5.avif" alt="Freedom Conclave 2026 event banner artwork" />
          </div>

          {/* ── DESKTOP ONLY: countdown at the top-right corner of the card ── */}
          <div className="ebc-desk-cd">
            <CountdownStrip />
          </div>
          
          <div className="ebc-desk-badge-wrap">
            <div className="ebc-badge">
              <img src="/Event/badge-icon.avif" alt="badge icon" style={{ width: "14px", height: "14px", objectFit: "contain", flexShrink: 0 }} />
              <span className="ebc-badge-text">EAST INDIA'S BIGGEST BUSINESS CONCLAVE</span>
            </div>
          </div>

          {/* ── MOBILE: badge row ── */}
          <div className="ebc-mob-toprow">
            <div className="ebc-badge">
              <img src="/Event/badge-icon.avif" alt="badge icon" style={{ width: "14px", height: "14px", objectFit: "contain", flexShrink: 0 }} />
              <span className="ebc-badge-text">EAST INDIA'S BIGGEST BUSINESS CONCLAVE</span>
            </div>
          </div>

          {/* ── TEXT ── */}
          <div className="ebc-text">
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "0.3rem" }}>
              <img src="/Event/ibhlogo.png" alt="IBH Logo" style={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0 }} />
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ display: "flex", alignItems: "baseline" }}>
                  <span style={{ fontSize: "16px", fontWeight: 600, color: "#f37c23", letterSpacing: "0.04em", fontFamily: "'Poppins', sans-serif" }}>INDIA</span>
                  <span style={{ fontSize: "16px", fontWeight: 600, color: "#002a9b", letterSpacing: "0.04em", fontFamily: "'Poppins', sans-serif", marginLeft: "5px" }}>BUSINESS</span>
                </div>
                <span style={{ display: "block", fontSize: "16px", fontWeight: 600, color: "#00a000", letterSpacing: "0.04em", fontFamily: "'Poppins', sans-serif" }}>HELPLINE</span>
              </div>
            </div>

            <p style={{ fontSize: "13px", color: "rgba(0,0,0,0.55)", fontStyle: "italic", fontWeight: 500, margin: "0.5rem 0 1rem", fontFamily: "'Inter', sans-serif" }}>
              Presents
            </p>

            <h2 className="ebc-title" style={{ fontSize: "32px", fontWeight: 800, color: "#002a9b", lineHeight: 1.12, letterSpacing: "-0.01em", margin: "0 0 0.5rem", fontFamily: "'Poppins', sans-serif" }}>
              Freedom Conclave <span style={{ color: "#f37c23" }}>2026</span>
            </h2>

            <p style={{ fontSize: "13.5px", color: "rgba(0,0,0,0.48)", margin: "0 0 0", fontWeight: 500, lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>
  Where founders, investors &amp; policymakers connect.
</p>

{/* ── RESCHEDULE ANNOUNCEMENT ── */}
<div className="ebc-notice">
  <span className="ebc-notice-icon">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f37c23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="13"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  </span>
  <span className="ebc-notice-text">
    Freedom Conclave 2026 has been rescheduled to <strong>19 December 2026</strong> due to unfortunate loss of our two founding members.
  </span>
</div>


            {/* ── COUNTDOWN: mobile only here (desktop version sits at the top-right corner) ── */}
            <div className="ebc-mob-cd">
              <CountdownStrip />
            </div>

            <div className="ebc-location" style={{ alignItems: "center", flexWrap: "wrap", fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Inter', sans-serif", paddingTop: "1rem" }}>
              <span className="ebc-date-group">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="3"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {formatEventDate(EVENT_DATE)}
              </span>
              <span className="ebc-place-group">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                TBD (Kolkata)
              </span>
            </div>
          </div>

          {/* ── MOBILE: location ── */}
          <div className="ebc-mob-location-row" style={{ alignItems: "center", fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Inter', sans-serif", paddingTop: "1rem" }}>
            <span className="ebc-date-group">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="3"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {formatEventDate(EVENT_DATE)}
            </span>
            <span className="ebc-place-group">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              TBD (Kolkata)
            </span>
          </div>

          {/* ── MOBILE: landmark image ── */}
          <div className="ebc-mob-img-wrap">
            <img src="/Event/Untitled design-5.avif" alt="Kolkata landmarks" />
          </div>

          {/* ── MOBILE: Know More button ── */}
          <a href="/mega-event" target="_blank" rel="noopener noreferrer" className="ebc-mob-btn">
            Know More
            <span className="ebc-btn-arrow">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </span>
          </a>

          {/* ── DESKTOP: Explore Event button ── */}
          <a href="/mega-event" target="_blank" rel="noopener noreferrer" className="ebc-desk-btn">
            Explore Event
            <span className="ebc-btn-arrow">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </span>
          </a>

        </div>
      </section>
    </>
  );
};

export default EventBanner;